/**
 * Imported data → table cell HTML, through a template cell's formatting. Used
 * by every path that fills a table from data: CSV / linked-sheet imports and
 * syncs, "Make static", and a `data` variable's matrix in the editor and the
 * reader — so a value looks the same whichever way it arrived.
 *
 * Formatting: the value replaces the template's first text run inside the
 * same block + inline wrappers (bold, colour, size…), the trick calc cells use
 * (render-calc-formatting.ts). Other runs and blocks are dropped.
 *
 * Links come from the data only, never from the template: a value that is a
 * web or mail URL, or a `{ text, href }` value (a hyperlinked Google Sheets
 * cell), is linked; any other value drops the template's link, so a row never
 * keeps the previous row's URL. A bare URL written into a link cell keeps the
 * cell's own label ("LinkedIn") and only takes the new address.
 *
 * Self-contained (no `@editor` imports) so it mirrors cleanly to the reader
 * repo. Without a DOM (server render) it falls back to plain markup.
 */

export type TableCellValue = string | { text: string; href: string };

const LINK_RE = /^(?:https?:\/\/|mailto:)[^\s<>"]+$/i;
const TEXT = "__sd_cell_text__";
const HREF = "__sd_cell_href__";
const PLAIN = `<p>${TEXT}</p>`;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

type CellTemplate = {
  /** The template with its first run as TEXT and no link. */
  text: string;
  /** The same run linked to HREF: the template's own anchor, or a new one. */
  link: string;
  /** A link cell's own label ("LinkedIn"); null when that label is a URL. */
  label: string | null;
};

const compile = (html: string): CellTemplate | null => {
  if (typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const block = doc.body.firstElementChild;
  if (!block) return null;
  const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let run = walker.nextNode();
  while (run && !run.nodeValue?.trim()) run = walker.nextNode();
  if (!run) return null;
  // Keep only the styled chain around that run.
  for (let node: Node = run; node !== block && node.parentNode; ) {
    const parent: Node = node.parentNode;
    for (const sibling of Array.from(parent.childNodes)) {
      if (sibling !== node) sibling.remove();
    }
    node = parent;
  }
  const anchor = run.parentElement?.closest("a") ?? null;
  const label =
    anchor && !LINK_RE.test(run.nodeValue?.trim() ?? "") ? run.nodeValue : null;
  run.nodeValue = TEXT;

  if (anchor) {
    anchor.setAttribute("href", HREF);
    const link = block.outerHTML;
    if (anchor === block) anchor.removeAttribute("href");
    else anchor.replaceWith(...Array.from(anchor.childNodes));
    return { text: block.outerHTML, link, label };
  }
  const text = block.outerHTML;
  if (text === PLAIN) return null;
  const link = doc.createElement("a");
  link.setAttribute("href", HREF);
  link.setAttribute("target", "_blank");
  run.parentNode?.replaceChild(link, run);
  link.appendChild(run);
  return { text, link: block.outerHTML, label: null };
};

// Per distinct template, not per cell: a 500-row import or a large `data`
// variable reuses one parse per column. A small cap bounds the map.
const MEMO_CAP = 500;
const memo = new Map<string, CellTemplate | null>();
const templateFor = (html: string): CellTemplate | null => {
  if (memo.has(html)) return memo.get(html) ?? null;
  const template = compile(html);
  memo.set(html, template);
  if (memo.size > MEMO_CAP) {
    const oldest = memo.keys().next().value;
    if (oldest !== undefined) memo.delete(oldest);
  }
  return template;
};

const fill = (html: string, text: string, href = ""): string =>
  html.split(TEXT).join(escapeHtml(text)).split(HREF).join(escapeHtml(href));

/** The HTML a cell keeps its formatting in: a formula cell's display format. */
export const cellTemplateHtml = (
  cell:
    | { content?: string; calculation?: unknown; calcFormatHtml?: string }
    | undefined,
): string | undefined =>
  cell?.calculation ? cell.calcFormatHtml || cell.content : cell?.content;

export const formatCellValue = (
  templateHtml: string | undefined,
  value: TableCellValue,
): string => {
  const text = typeof value === "string" ? value : value.text || value.href;
  const target = (typeof value === "string" ? value : value.href).trim();
  const href = LINK_RE.test(target) ? target : null;
  const html = templateHtml?.trim();
  const template = html ? templateFor(html) : null;

  if (!template) {
    if (text === "") return "";
    const body = escapeHtml(text);
    return href
      ? `<p><a href="${escapeHtml(href)}" target="_blank">${body}</a></p>`
      : `<p>${body}</p>`;
  }
  if (!href) return fill(template.text, text);
  const label =
    template.label !== null && text.trim() === href ? template.label : text;
  return fill(template.link, label, href);
};
